import { useState } from 'react';

function CopyableUrl({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — user can still select and copy manually.
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      title={copied ? 'Copied!' : 'Click to copy'}
      className="rounded bg-neutral-800 px-1.5 py-0.5 font-mono text-xs text-neutral-100 hover:bg-neutral-700"
    >
      {url}
      <span className="ml-1.5 text-neutral-400">{copied ? '✓ copied' : '⧉'}</span>
    </button>
  );
}

export default function Install() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="text-3xl font-semibold tracking-tight">Install the extension</h1>
      <p className="mt-2 text-sm text-neutral-400">
        Snippits isn&apos;t in the Chrome Web Store yet. For now, load it as an unpacked extension
        &mdash; takes about 30 seconds.
      </p>

      <a
        href="/snippits-extension.zip"
        download
        className="mt-6 inline-flex items-center rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        Download snippits-extension.zip
      </a>

      <ol className="mt-8 space-y-4 text-sm text-neutral-200">
        <li className="flex gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs">
            1
          </span>
          Download the zip above and unzip it somewhere you won&apos;t lose it.
        </li>
        <li className="flex gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs">
            2
          </span>
          Open <CopyableUrl url="chrome://extensions" /> in a new tab (Chrome blocks direct
          links to internal pages — click to copy, then paste into the address bar).
        </li>
        <li className="flex gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs">
            3
          </span>
          Flip on <strong>Developer mode</strong> (top-right corner).
        </li>
        <li className="flex gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs">
            4
          </span>
          Click <strong>Load unpacked</strong> and pick the unzipped folder.
        </li>
        <li className="flex gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs">
            5
          </span>
          Click the Snippits icon in the toolbar to capture your first screenshot.
        </li>
      </ol>

      <p className="mt-8 text-xs text-neutral-500">
        When new versions ship, re-download this zip, replace the folder, and click the refresh
        icon next to the extension on the chrome://extensions page.
      </p>
    </div>
  );
}
